import { 
  AuthChallenge, 
  LoginRequest, 
  AuthResponse, 
  Profile, 
  GitHubAuthUrl, 
  GitHubConnectRequest, 
  DAG, 
  CreateDAGRequest, 
  CreateDAGResponse, 
  DAGWithProposals,
  DetailedProposal,
  CreateProposalRequest,
  CreateProposalResponse,
  VoteRequest,
  DetailedWork,
  CreateWorkRequest,
  CreateWorkResponse,
  InstructionResponse,
  ApiError,
  ProposalVoteHistory
} from '~/types/api'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev.api.dag.gg/api'

class ApiServiceError extends Error {
  constructor(message: string, public status?: number) {
    super(message)
    this.name = 'ApiServiceError'
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Network error' }))
    throw new ApiServiceError(errorData.message || `HTTP ${response.status}`, response.status)
  }
  return response.json()
}

const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  requiresAuth = false
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`
  
  const makeRequest = async (token?: string): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (requiresAuth && token) {
      headers.Authorization = `Bearer ${token}`
    }

    return fetch(url, {
      ...options,
      headers,
    })
  }

  // Step 1: Try with current access token
  let token = requiresAuth ? localStorage.getItem('access_token') : null
  let response = await makeRequest(token || undefined)

  // Step 2: If unauthorized and we have a refresh token, try to refresh
  if (response.status === 401 && requiresAuth && token) {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        // Get new tokens using refresh token
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        })

        if (refreshResponse.ok) {
          const authData: AuthResponse = await refreshResponse.json()
          
          // Store new tokens
          localStorage.setItem('access_token', authData.accessToken)
          localStorage.setItem('refresh_token', authData.refreshToken)
          localStorage.setItem('user_data', JSON.stringify(authData.user))
          
          // Step 3: Retry original request with new access token
          response = await makeRequest(authData.accessToken)
        } else {
          // If refresh fails, clear tokens and throw error
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('user_data')
          throw new ApiServiceError('Session expired. Please login again.', 401)
        }
      } catch (refreshError) {
        // Clear tokens on refresh error
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_data')
        
        if (refreshError instanceof ApiServiceError) {
          throw refreshError
        }
        throw new ApiServiceError('Failed to refresh session. Please login again.', 401)
      }
    } else {
      // No refresh token available
      localStorage.removeItem('access_token')
      localStorage.removeItem('user_data')
      throw new ApiServiceError('No refresh token available. Please login again.', 401)
    }
  }

  return handleResponse<T>(response)
}

export const api = {
  // Health & System
  health: () => apiRequest<{ status: string }>('/health'),
  
  root: () => apiRequest<{ title: string; version: string; status: string }>('/'),

  // Authentication
  auth: {
    challenge: (walletAddress: string) =>
      apiRequest<AuthChallenge>('/auth/challenge', {
        method: 'POST',
        body: JSON.stringify({ walletAddress }),
      }),

    login: (loginData: LoginRequest) =>
      apiRequest<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginData),
      }),

    refreshToken: (refreshToken: string) =>
      apiRequest<AuthResponse>('/auth/refresh-token', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),

    logout: (refreshToken: string) =>
      apiRequest<{ success: boolean; message: string }>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
  },

  // Profile
  profile: {
    get: () => apiRequest<Profile>('/profile', {}, true),

    github: {
      getAuthUrl: (redirectUrl: string) => apiRequest<GitHubAuthUrl>(`/profile/github/auth-url?redirectUrl=${encodeURIComponent(redirectUrl)}`),
      
      connect: (githubCode: string) =>
        apiRequest<{ message: string; user: Profile }>('/profile/github/connect', {
          method: 'POST',
          body: JSON.stringify({ githubCode }),
        }, true),
    },
  },

  // DAGs
  dags: {
    getAll: () => apiRequest<DAG[]>('/dags'),

    getById: (dagId: string) => apiRequest<DAGWithProposals>(`/dags/${dagId}`),

    getByRepositoryName: (repositoryName: string) => 
      apiRequest<DAGWithProposals>(`/dags/repository/${repositoryName}`),

    create: (dagData: CreateDAGRequest) =>
      apiRequest<CreateDAGResponse>('/dags', {
        method: 'POST',
        body: JSON.stringify(dagData),
      }, true),

    addCollaborator: (dagId: string) =>
      apiRequest<{ message: string }>(`/dags/${dagId}/collaborators`, {
        method: 'POST',
      }, true),
  },

  // Proposals
  proposals: {
    getById: (proposalId: string) => 
      apiRequest<DetailedProposal>(`/proposals/${proposalId}`),

    getByRepositoryAndIssue: (repositoryName: string, issueNumber: number) =>
      apiRequest<DetailedProposal>(`/proposals/repository/${repositoryName}/issue/${issueNumber}`),

    create: (proposalData: CreateProposalRequest) =>
      apiRequest<CreateProposalResponse>('/proposals', {
        method: 'POST',
        body: JSON.stringify(proposalData),
      }, true),

    vote: (proposalId: string, voteData: VoteRequest) =>
      apiRequest<{ 
        message: string; 
        proposal: {
          proposalId: string;
          status: string;
          approveVotesFor: number;
          approveVotesAgainst: number;
          deleteVotesFor: number;
          deleteVotesAgainst: number;
        }
      }>(`/proposals/${proposalId}/vote`, {
        method: 'POST',
        body: JSON.stringify(voteData),
      }, true),

    getVoteHistory: (proposalId: string) =>
      apiRequest<ProposalVoteHistory>(`/proposals/${proposalId}/vote-history`, {}, true),
  },

  // Works
  works: {
    getById: (workId: string) => 
      apiRequest<DetailedWork>(`/works/${workId}`),

    getByProposalAndWorker: (proposalId: string, walletAddress: string) =>
      apiRequest<{
        works: Array<{
          workId: string
          pda: string
          proposalId: string
          proposalTitle: string
          dagId: string
          dagName: string
          worker: string
          title: string
          description: string
          head: string
          githubPullRequestUrl: string
          status: string
          approveVotesFor: string
          approveVotesAgainst: string
          createdAt: string
        }>
        totalCount: number
      }>(`/works/proposal/${proposalId}/worker/${walletAddress}`),

    create: (workData: CreateWorkRequest) =>
      apiRequest<CreateWorkResponse>('/works', {
        method: 'POST',
        body: JSON.stringify(workData),
      }, true),

    open: (workId: string) =>
      apiRequest<InstructionResponse>(`/works/${workId}/open`, {
        method: 'POST',
      }, true),

    merge: (workId: string) =>
      apiRequest<InstructionResponse>(`/works/${workId}/merge`, {
        method: 'POST',
      }, true),

    vote: (workId: string, voteDirection: 'for' | 'against') =>
      apiRequest<{
        message: string;
        work: {
          workId: string;
          status: string;
          votesFor: number;
          votesAgainst: number;
        }
      }>(`/works/${workId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ voteDirection }),
      }, true),
  },

  // Games (DAGs)
  games: {
    getAll: () => apiRequest<Array<{
      dagId: string;
      dagName: string;
      tokenAddress: string;
      githubRepositoryUrl: string;
      createdAt: string;
    }>>('/dags'),

    getById: (gameId: string) => apiRequest<{
      dagId: string;
      dagName: string;
      tokenAddress: string;
      githubRepositoryUrl: string;
      createdAt: string;
      proposals: Array<{
        proposalId: string;
        title: string;
        githubIssueUrl: string;
        status: string;
        bountyBps: number;
        createdAt: string;
        approveVotesFor: string;
        approveVotesAgainst: string;
      }>;
    }>(`/dags/${gameId}`),

    getByRepositoryName: (repositoryName: string) => apiRequest<{
      dagId: string;
      dagName: string;
      tokenAddress: string;
      githubRepositoryUrl: string;
      createdAt: string;
      proposals: Array<{
        proposalId: string;
        title: string;
        githubIssueUrl: string;
        status: string;
        bountyBps: number;
        createdAt: string;
        approveVotesFor: string;
        approveVotesAgainst: string;
      }>;
    }>(`/dags/repository/${repositoryName}`),
  },
} 